import type {
  AmendmentState,
  BuiltInViewId,
  CanonicalTransaction,
  InsiderRole,
  MissingFieldFlag,
  PlanStatus,
  ScreenerDensity,
  ScreenerFilterState,
  ScreenerPageSize,
  ScreenerSort,
  ScreenerSortField,
  ScreenerState,
  SavedViewStateV1,
  SecurityStatus,
  SignalBucket,
  SignalConfidence,
  TradeType,
  ValueQuality
} from "./types";

export interface BuiltInViewDefinition {
  readonly id: BuiltInViewId;
  readonly label: string;
  readonly defaultSort: readonly Readonly<ScreenerSort>[];
}

export type DeepReadonly<T> = T extends (...args: never[]) => unknown
  ? T
  : T extends readonly (infer Item)[]
    ? readonly DeepReadonly<Item>[]
    : T extends object
      ? { readonly [Key in keyof T]: DeepReadonly<T[Key]> }
      : T;

function deepFreeze<T>(value: T): DeepReadonly<T> {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const nested of Object.values(value as Record<string, unknown>)) deepFreeze(nested);
    Object.freeze(value);
  }
  return value as DeepReadonly<T>;
}

const freezeSorts = (...sorts: ScreenerSort[]): readonly Readonly<ScreenerSort>[] =>
  Object.freeze(sorts.map((sort) => Object.freeze({ ...sort })));

export const BUILT_IN_VIEWS = Object.freeze({
  "all-activity": Object.freeze({
    id: "all-activity",
    label: "All activity",
    defaultSort: freezeSorts({ id: "filingDate", desc: true })
  }),
  today: Object.freeze({
    id: "today",
    label: "Today",
    defaultSort: freezeSorts(
      { id: "filingDate", desc: true },
      { id: "score", desc: true }
    )
  }),
  "action-queue": Object.freeze({
    id: "action-queue",
    label: "Action queue",
    defaultSort: freezeSorts(
      { id: "score", desc: true },
      { id: "value", desc: true },
      { id: "filingDate", desc: true }
    )
  }),
  "informative-buys": Object.freeze({
    id: "informative-buys",
    label: "Informative buys",
    defaultSort: freezeSorts(
      { id: "score", desc: true },
      { id: "filingDate", desc: true }
    )
  }),
  "cluster-buys": Object.freeze({
    id: "cluster-buys",
    label: "Cluster buys",
    defaultSort: freezeSorts(
      { id: "distinctInsiderCount", desc: true },
      { id: "value", desc: true }
    )
  }),
  "large-purchases": Object.freeze({
    id: "large-purchases",
    label: "Large purchases",
    defaultSort: freezeSorts({ id: "value", desc: true })
  }),
  "unplanned-sells": Object.freeze({
    id: "unplanned-sells",
    label: "Unplanned sells",
    defaultSort: freezeSorts({ id: "value", desc: true })
  }),
  "needs-review": Object.freeze({
    id: "needs-review",
    label: "Needs review",
    defaultSort: freezeSorts({ id: "filingDate", desc: true })
  }),
  "my-watchlist": Object.freeze({
    id: "my-watchlist",
    label: "My watchlist",
    defaultSort: freezeSorts({ id: "filingDate", desc: true })
  })
}) satisfies Readonly<Record<BuiltInViewId, BuiltInViewDefinition>>;

export const SCREENER_COLUMN_IDS = Object.freeze([
  "favorite",
  "ticker",
  "exchange",
  "companyName",
  "industryDescription",
  "insiderName",
  "relationshipFlags",
  "signal",
  "confidence",
  "filingDate",
  "tradeDate",
  "filingLagDays",
  "shares",
  "price",
  "value",
  "holdingsBefore",
  "holdingsAfter",
  "ownershipChangePct",
  "materialityBps",
  "distinctInsiderCount",
  "transactionCode",
  "planStatus",
  "securityStatus",
  "valueQuality",
  "flags",
  "score",
  "details"
] as const);

export const MAX_SCREENER_SORTS = 8;

const EMPTY_FILTERS: ScreenerFilterState = {
  query: "",
  filingDateFrom: null,
  filingDateTo: null,
  tradeDateFrom: null,
  tradeDateTo: null,
  signalBuckets: [],
  relationshipFlags: [],
  minTransactionValue: null,
  maxTransactionValue: null,
  planStatuses: [],
  minDistinctInsiders: null,
  exchanges: [],
  transactionActivities: [],
  transactionCodes: [],
  securityStatuses: [],
  minShares: null,
  maxShares: null,
  minPrice: null,
  maxPrice: null,
  minHoldingsChange: null,
  maxHoldingsChange: null,
  confidences: [],
  amendmentStates: [],
  valueQualities: [],
  missingFieldFlags: [],
  industryDescription: "",
  minFilingLagDays: null,
  maxFilingLagDays: null,
  accessionNumber: "",
  favoritesOnly: false,
  marketCapBuckets: [],
  minValueToMarketCapBps: null
};

// Trader-default table density: high-signal research columns only.
// Explicit false values matter — empty visibility historically meant "show everything" on wide screens.
export const DEFAULT_COLUMN_VISIBILITY: DeepReadonly<Record<(typeof SCREENER_COLUMN_IDS)[number], boolean>> =
  deepFreeze({
    favorite: true,
    ticker: true,
    exchange: false,
    companyName: false,
    industryDescription: false,
    insiderName: true,
    relationshipFlags: true,
    signal: true,
    confidence: false,
    filingDate: true,
    tradeDate: false,
    filingLagDays: false,
    shares: true,
    price: true,
    value: true,
    holdingsBefore: true,
    holdingsAfter: true,
    ownershipChangePct: true,
    materialityBps: true,
    distinctInsiderCount: false,
    transactionCode: false,
    planStatus: false,
    securityStatus: false,
    valueQuality: false,
    flags: false,
    score: true,
    details: true
  });

export const DEFAULT_SCREENER_STATE: DeepReadonly<ScreenerState> = deepFreeze({
  filters: {
    ...EMPTY_FILTERS,
    // Prefer tradable common-equity discretionary activity by default.
    securityStatuses: ["common_equity"],
    minTransactionValue: 25_000
  },
  sorting: BUILT_IN_VIEWS["informative-buys"].defaultSort.map((sort) => ({ ...sort })),
  page: 0,
  pageSize: 100,
  density: "compact",
  columnVisibility: { ...DEFAULT_COLUMN_VISIBILITY },
  columnOrder: [...SCREENER_COLUMN_IDS],
  columnPinning: {
    // companyName is off by default, so do not pin hidden columns.
    left: ["favorite", "ticker"],
    right: ["score", "details"]
  },
  builtInViewId: "informative-buys",
  savedViewId: null,
  selectedTransactionId: null
} satisfies ScreenerState);

// Sets are expected to hold uppercase tickers (applyCanonicalScreener builds them
// that way once per screen); arrays are normalized here since they only reach this
// path from defaults and tests.
function favoriteContains(
  favoriteTickers: ReadonlySet<string> | readonly string[],
  ticker: string
): boolean {
  const favorites =
    favoriteTickers instanceof Set
      ? (favoriteTickers as ReadonlySet<string>)
      : new Set(Array.from(favoriteTickers, (favoriteTicker) => favoriteTicker.toUpperCase()));
  return favorites.has(ticker.toUpperCase());
}

export function matchesBuiltInView(
  id: BuiltInViewId,
  row: CanonicalTransaction,
  favoriteTickers: ReadonlySet<string> | readonly string[] = []
): boolean {
  switch (id) {
    case "all-activity":
      return true;
    case "today": {
      // Prefer matchesBuiltInViewWithContext so lookback is relative to dataset max filing date.
      // Without context, keep only high-signal buckets to avoid dumping the whole table.
      return (
        row.signal.bucket === "informative_buy" ||
        row.signal.bucket === "informative_sell" ||
        row.signal.bucket === "needs_review"
      );
    }
    case "action-queue": {
      const isBuy = row.signal.bucket === "informative_buy";
      if (!isBuy) return false;
      const score = row.signal.score;
      const highScore = score !== null && score >= 75;
      const large = row.value !== null && row.value >= 100_000;
      const ceoCfo =
        row.relationshipFlags.includes("CEO") || row.relationshipFlags.includes("CFO");
      const clustered =
        row.cluster !== null && row.cluster.distinctInsiderCount >= 2;
      const materialBps =
        row.marketContext.valueToMarketCapBps !== null &&
        row.marketContext.valueToMarketCapBps >= 5;
      return highScore || large || ceoCfo || clustered || materialBps;
    }
    case "informative-buys":
      return row.signal.bucket === "informative_buy";
    case "cluster-buys":
      return (
        row.signal.bucket === "informative_buy" &&
        row.cluster !== null &&
        row.cluster.distinctInsiderCount >= 2
      );
    case "large-purchases":
      return (
        row.signal.bucket === "informative_buy" &&
        (row.valueQuality === "reported" || row.valueQuality === "derived") &&
        row.value !== null &&
        row.value >= 1_000_000
      );
    case "unplanned-sells":
      // informative_sell already excludes planned/10b5-1 rows at signal classification time.
      // Keep planned out defensively; treat unknown plan status as actionable discretionary sell.
      return row.signal.bucket === "informative_sell" && row.plan.status !== "planned";
    case "needs-review":
      return row.signal.bucket === "needs_review";
    case "my-watchlist":
      return favoriteContains(favoriteTickers, row.ticker);
  }
}

/** Days of filing lag from the dataset's newest filing date that still count as "Today". */
export const TODAY_VIEW_LOOKBACK_DAYS = 2;

export function matchesBuiltInViewWithContext(
  id: BuiltInViewId,
  row: CanonicalTransaction,
  favoriteTickers: ReadonlySet<string> | readonly string[] = [],
  context: { latestFilingDate?: string | null } = {}
): boolean {
  if (id === "today") {
    const latest = context.latestFilingDate ?? null;
    if (!latest) return row.signal.bucket === "informative_buy" || row.signal.bucket === "informative_sell";
    const latestMs = Date.parse(`${latest}T00:00:00Z`);
    const rowMs = Date.parse(`${row.filingDate}T00:00:00Z`);
    if (!Number.isFinite(latestMs) || !Number.isFinite(rowMs)) return false;
    const dayMs = 24 * 60 * 60 * 1000;
    const ageDays = Math.floor((latestMs - rowMs) / dayMs);
    if (ageDays < 0 || ageDays > TODAY_VIEW_LOOKBACK_DAYS) return false;
    return (
      row.signal.bucket === "informative_buy" ||
      row.signal.bucket === "informative_sell" ||
      row.signal.bucket === "needs_review"
    );
  }
  return matchesBuiltInView(id, row, favoriteTickers);
}

/** Higher is better for Action queue desk ordering. */
export function actionQueueRank(row: CanonicalTransaction): number {
  if (row.signal.bucket !== "informative_buy") return Number.NEGATIVE_INFINITY;
  const score = row.signal.score ?? 0;
  const value = row.value ?? 0;
  const bps = row.marketContext.valueToMarketCapBps ?? 0;
  const ceoCfo =
    row.relationshipFlags.includes("CEO") || row.relationshipFlags.includes("CFO") ? 1 : 0;
  const clustered =
    row.cluster !== null && row.cluster.distinctInsiderCount >= 2
      ? row.cluster.distinctInsiderCount
      : 0;
  // Weighted composite used as secondary sort after explicit score/value sorts.
  return score * 1_000_000 + Math.log10(Math.max(value, 1)) * 10_000 + bps * 100 + ceoCfo * 5_000 + clustered * 1_000;
}


const SIGNAL_BUCKETS = [
  "informative_buy",
  "informative_sell",
  "uninformative_buy",
  "uninformative_sell",
  "needs_review"
] as const satisfies readonly SignalBucket[];
const RELATIONSHIP_FLAGS = [
  "CEO",
  "CFO",
  "Director",
  "Officer",
  "10% Owner",
  "Other"
] as const satisfies readonly InsiderRole[];
const PLAN_STATUSES = ["planned", "not_planned", "unknown"] as const satisfies readonly PlanStatus[];
const ACTIVITIES = ["buy", "sell", "option", "award"] as const satisfies readonly TradeType[];
const SECURITY_STATUSES = [
  "common_equity",
  "non_common",
  "derivative",
  "unknown"
] as const satisfies readonly SecurityStatus[];
const CONFIDENCES = ["high", "medium", "low"] as const satisfies readonly SignalConfidence[];
const AMENDMENT_STATES = ["original", "amendment"] as const satisfies readonly AmendmentState[];
const VALUE_QUALITIES = [
  "reported",
  "derived",
  "missing",
  "suspect"
] as const satisfies readonly ValueQuality[];
const MISSING_FIELDS = [
  "tradeDate",
  "price",
  "value",
  "holdingsBefore",
  "holdingsAfter",
  "ownerCik",
  "ownerIdentityKey",
  "filingLagDays"
] as const satisfies readonly MissingFieldFlag[];
export const SCREENER_SORT_FIELDS = [
  "filingDate",
  "tradeDate",
  "ticker",
  "companyName",
  "insiderName",
  "value",
  "shares",
  "price",
  "ownershipChangePct",
  "materialityBps",
  "filingLagDays",
  "score",
  "confidence",
  "distinctInsiderCount"
] as const satisfies readonly ScreenerSortField[];
const SORT_FIELDS = SCREENER_SORT_FIELDS;

export function isScreenerSortField(value: string): value is ScreenerSortField {
  return (SCREENER_SORT_FIELDS as readonly string[]).includes(value);
}
const BUILT_IN_VIEW_IDS = Object.keys(BUILT_IN_VIEWS) as BuiltInViewId[];
export const SCREENER_PAGE_SIZES = [50, 100, 250] as const satisfies readonly ScreenerPageSize[];
const PAGE_SIZES = SCREENER_PAGE_SIZES;

export function isScreenerPageSize(value: number): value is ScreenerPageSize {
  return (SCREENER_PAGE_SIZES as readonly number[]).includes(value);
}
const DENSITIES = ["compact", "comfortable"] as const satisfies readonly ScreenerDensity[];
const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "prototype", "constructor"]);
const SCREENER_COLUMN_ID_SET = new Set<string>(SCREENER_COLUMN_IDS);
const MAX_RAW_PARAM_LENGTH = 4_096;
const MAX_TEXT_LENGTH = 256;
const MAX_ID_LENGTH = 256;
const MAX_ARRAY_ITEMS = 64;
const MAX_ARRAY_ITEM_LENGTH = 128;
const MAX_PAGE = 100_000;

function cloneFilters(filters: DeepReadonly<ScreenerFilterState>): ScreenerFilterState {
  return {
    ...filters,
    signalBuckets: [...filters.signalBuckets],
    relationshipFlags: [...filters.relationshipFlags],
    planStatuses: [...filters.planStatuses],
    exchanges: [...filters.exchanges],
    transactionActivities: [...filters.transactionActivities],
    transactionCodes: [...filters.transactionCodes],
    securityStatuses: [...filters.securityStatuses],
    confidences: [...filters.confidences],
    amendmentStates: [...filters.amendmentStates],
    valueQualities: [...filters.valueQualities],
    missingFieldFlags: [...filters.missingFieldFlags],
    marketCapBuckets: [...(filters.marketCapBuckets ?? [])]
  };
}

export function createDefaultScreenerState(): ScreenerState {
  return {
    ...DEFAULT_SCREENER_STATE,
    filters: cloneFilters(DEFAULT_SCREENER_STATE.filters),
    sorting: DEFAULT_SCREENER_STATE.sorting.map((sort) => ({ ...sort })),
    columnVisibility: { ...DEFAULT_SCREENER_STATE.columnVisibility },
    columnOrder: [...DEFAULT_SCREENER_STATE.columnOrder],
    columnPinning: {
      left: [...DEFAULT_SCREENER_STATE.columnPinning.left],
      right: [...DEFAULT_SCREENER_STATE.columnPinning.right]
    }
  };
}

export function captureSavedViewState(
  state: DeepReadonly<ScreenerState>
): SavedViewStateV1 {
  return {
    version: 1,
    builtInViewId: state.builtInViewId,
    filters: cloneFilters(state.filters),
    sorting: state.sorting.map((sort) => ({ ...sort })),
    density: state.density,
    columnVisibility: { ...state.columnVisibility },
    columnOrder: [...state.columnOrder],
    columnPinning: {
      left: [...state.columnPinning.left],
      right: [...state.columnPinning.right]
    }
  };
}

export function applySavedView(
  current: DeepReadonly<ScreenerState>,
  savedView: DeepReadonly<SavedViewStateV1>,
  savedViewId: string
): ScreenerState {
  return {
    ...current,
    filters: cloneFilters(savedView.filters),
    sorting: savedView.sorting.map((sort) => ({ ...sort })),
    density: savedView.density,
    columnVisibility: { ...savedView.columnVisibility },
    columnOrder: [...savedView.columnOrder],
    columnPinning: {
      left: [...savedView.columnPinning.left],
      right: [...savedView.columnPinning.right]
    },
    builtInViewId: savedView.builtInViewId,
    savedViewId,
    page: 0,
    selectedTransactionId: null
  };
}

function uniqueSorted<T extends string>(values: readonly T[]): T[] {
  return [...new Set(values)].sort();
}

function uniqueInOrder<T extends string>(values: readonly T[]): T[] {
  const seen = new Set<T>();
  return values.filter((value) => {
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

export function limitScreenerSorts<T extends { id: string; desc: boolean }>(
  values: readonly T[]
): T[] {
  const seen = new Set<string>();
  const limited: T[] = [];
  for (const value of values) {
    if (seen.has(value.id)) continue;
    seen.add(value.id);
    limited.push(value);
    if (limited.length === MAX_SCREENER_SORTS) break;
  }
  return limited;
}

function normalizeSorts(values: readonly ScreenerSort[]): ScreenerSort[] | null {
  const seen = new Set<ScreenerSortField>();
  const normalized: ScreenerSort[] = [];
  for (const value of values) {
    if (
      typeof value.id !== "string" ||
      value.id.length > MAX_ARRAY_ITEM_LENGTH ||
      !SORT_FIELDS.includes(value.id) ||
      typeof value.desc !== "boolean"
    ) {
      return null;
    }
    if (seen.has(value.id)) continue;
    seen.add(value.id);
    normalized.push({ id: value.id, desc: value.desc });
  }
  return limitScreenerSorts(normalized);
}

function normalizeVisibility(
  values: Readonly<Record<string, boolean>>
): Record<string, boolean> | null {
  const entries = Object.entries(values);
  if (
    entries.length > MAX_ARRAY_ITEMS ||
    entries.some(
      ([key, value]) =>
        key.length === 0 ||
        key.length > MAX_ARRAY_ITEM_LENGTH ||
        UNSAFE_OBJECT_KEYS.has(key) ||
        !SCREENER_COLUMN_ID_SET.has(key) ||
        typeof value !== "boolean"
    )
  ) {
    return null;
  }
  const normalized: Record<string, boolean> = {};
  for (const [key, value] of entries.sort(([left], [right]) => left.localeCompare(right))) {
    normalized[key] = value;
  }
  return normalized;
}

function normalizeBoundedSet(
  values: readonly string[],
  uppercase = false
): string[] | null {
  if (values.length > MAX_ARRAY_ITEMS) return null;
  const normalized: string[] = [];
  for (const value of values) {
    if (typeof value !== "string" || value.length === 0 || value.length > MAX_ARRAY_ITEM_LENGTH) {
      return null;
    }
    const candidate = uppercase ? value.trim().toUpperCase() : value;
    if (candidate.length === 0) return null;
    normalized.push(candidate);
  }
  return uniqueSorted(normalized);
}

function normalizeColumnIds(values: readonly string[]): string[] | null {
  if (
    values.length > MAX_ARRAY_ITEMS ||
    values.some(
      (value) =>
        typeof value !== "string" ||
        value.length === 0 ||
        value.length > MAX_ARRAY_ITEM_LENGTH ||
        UNSAFE_OBJECT_KEYS.has(value) ||
        !SCREENER_COLUMN_ID_SET.has(value)
    )
  ) {
    return null;
  }
  return uniqueInOrder(values);
}

function normalizeColumnOrder(values: readonly string[]): string[] | null {
  const normalized = normalizeColumnIds(values);
  if (normalized === null) return null;
  const included = new Set(normalized);
  return [
    ...normalized,
    ...SCREENER_COLUMN_IDS.filter((columnId) => !included.has(columnId))
  ];
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function setJson(params: URLSearchParams, key: string, value: unknown): void {
  const serialized = JSON.stringify(value);
  if (serialized.length <= MAX_RAW_PARAM_LENGTH) params.set(key, serialized);
}

function setBoundedText(
  params: URLSearchParams,
  key: string,
  value: string,
  maxLength: number
): void {
  if (value !== "" && value.length <= maxLength) params.set(key, value);
}

function setNullableNumber(params: URLSearchParams, key: string, value: number | null): void {
  if (value !== null && Number.isFinite(value)) params.set(key, String(value));
}

export function serializeScreenerState(state: ScreenerState): string {
  const params = new URLSearchParams();
  params.set("v", "1");
  const filters = state.filters;

  setBoundedText(params, "q", filters.query, MAX_TEXT_LENGTH);
  if (filters.filingDateFrom !== null) {
    setBoundedText(params, "filing_from", filters.filingDateFrom, MAX_ARRAY_ITEM_LENGTH);
  }
  if (filters.filingDateTo !== null) {
    setBoundedText(params, "filing_to", filters.filingDateTo, MAX_ARRAY_ITEM_LENGTH);
  }
  if (filters.tradeDateFrom !== null) {
    setBoundedText(params, "trade_from", filters.tradeDateFrom, MAX_ARRAY_ITEM_LENGTH);
  }
  if (filters.tradeDateTo !== null) {
    setBoundedText(params, "trade_to", filters.tradeDateTo, MAX_ARRAY_ITEM_LENGTH);
  }

  const setArrays: Array<[string, string[] | null]> = [
    ["signals", normalizeBoundedSet(filters.signalBuckets)],
    ["relationships", normalizeBoundedSet(filters.relationshipFlags)],
    ["plans", normalizeBoundedSet(filters.planStatuses)],
    ["exchanges", normalizeBoundedSet(filters.exchanges, true)],
    ["activities", normalizeBoundedSet(filters.transactionActivities)],
    ["codes", normalizeBoundedSet(filters.transactionCodes, true)],
    ["securities", normalizeBoundedSet(filters.securityStatuses)],
    ["confidences", normalizeBoundedSet(filters.confidences)],
    ["amendments", normalizeBoundedSet(filters.amendmentStates)],
    ["qualities", normalizeBoundedSet(filters.valueQualities)],
    ["missing", normalizeBoundedSet(filters.missingFieldFlags)]
  ];
  for (const [key, values] of setArrays) {
    if (values !== null && values.length > 0) setJson(params, key, values);
  }

  setNullableNumber(params, "value_min", filters.minTransactionValue);
  setNullableNumber(params, "value_max", filters.maxTransactionValue);
  setNullableNumber(params, "insiders_min", filters.minDistinctInsiders);
  setNullableNumber(params, "shares_min", filters.minShares);
  setNullableNumber(params, "shares_max", filters.maxShares);
  setNullableNumber(params, "price_min", filters.minPrice);
  setNullableNumber(params, "price_max", filters.maxPrice);
  setNullableNumber(params, "holdings_min", filters.minHoldingsChange);
  setNullableNumber(params, "holdings_max", filters.maxHoldingsChange);
  setNullableNumber(params, "lag_min", filters.minFilingLagDays);
  setNullableNumber(params, "lag_max", filters.maxFilingLagDays);
  setBoundedText(params, "industry", filters.industryDescription, MAX_TEXT_LENGTH);
  setBoundedText(params, "accession", filters.accessionNumber, MAX_ID_LENGTH);
  if (filters.favoritesOnly) params.set("favorites", "1");
  const marketCapBuckets = filters.marketCapBuckets ?? [];
  if (marketCapBuckets.length > 0) {
    setJson(params, "mcap", normalizeBoundedSet(marketCapBuckets));
  }
  setNullableNumber(params, "mcap_bps_min", filters.minValueToMarketCapBps ?? null);

  const viewDefinition = BUILT_IN_VIEWS[state.builtInViewId] ?? BUILT_IN_VIEWS["all-activity"];
  const viewDefaultSort = viewDefinition.defaultSort;
  const sorting = normalizeSorts(state.sorting) ?? viewDefaultSort.map((sort) => ({ ...sort }));
  if (!sameJson(sorting, viewDefaultSort)) setJson(params, "sort", sorting);
  if (
    state.page !== 0 &&
    Number.isSafeInteger(state.page) &&
    state.page > 0 &&
    state.page <= MAX_PAGE
  ) {
    params.set("page", String(state.page));
  }
  if (state.pageSize !== DEFAULT_SCREENER_STATE.pageSize) {
    params.set("page_size", String(state.pageSize));
  }
  if (state.density !== DEFAULT_SCREENER_STATE.density) params.set("density", state.density);

  const visibility = normalizeVisibility(state.columnVisibility);
  const defaultVisibility = normalizeVisibility(DEFAULT_SCREENER_STATE.columnVisibility);
  if (
    visibility !== null &&
    Object.keys(visibility).length > 0 &&
    !sameJson(visibility, defaultVisibility)
  ) {
    setJson(params, "columns", visibility);
  }
  const columnOrder = normalizeColumnOrder(state.columnOrder);
  if (columnOrder !== null && !sameJson(columnOrder, DEFAULT_SCREENER_STATE.columnOrder)) {
    setJson(params, "column_order", columnOrder);
  }
  const pinLeft = normalizeColumnIds(state.columnPinning.left);
  const pinRight = normalizeColumnIds(state.columnPinning.right);
  if (pinLeft !== null && !sameJson(pinLeft, DEFAULT_SCREENER_STATE.columnPinning.left)) {
    setJson(params, "pin_left", pinLeft);
  }
  if (pinRight !== null && !sameJson(pinRight, DEFAULT_SCREENER_STATE.columnPinning.right)) {
    setJson(params, "pin_right", pinRight);
  }

  if (state.builtInViewId !== DEFAULT_SCREENER_STATE.builtInViewId) {
    params.set("view", state.builtInViewId);
  }
  if (state.savedViewId !== null) {
    setBoundedText(params, "saved", state.savedViewId, MAX_ID_LENGTH);
  }
  if (state.selectedTransactionId !== null && state.selectedTransactionId !== "") {
    setBoundedText(params, "tx", state.selectedTransactionId, MAX_ID_LENGTH);
  }
  return params.toString();
}

function validDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function boundedParam(
  params: URLSearchParams,
  key: string,
  maxLength = MAX_RAW_PARAM_LENGTH
): string | null {
  const value = params.get(key);
  return value !== null && value.length <= Math.min(maxLength, MAX_RAW_PARAM_LENGTH)
    ? value
    : null;
}

function parseBoundedText(
  params: URLSearchParams,
  key: string,
  fallback: string,
  maxLength: number
): string {
  return boundedParam(params, key, maxLength) ?? fallback;
}

function parseDateParam(
  params: URLSearchParams,
  key: string,
  fallback: string | null
): string | null {
  const value = boundedParam(params, key, MAX_ARRAY_ITEM_LENGTH);
  return value === null || !validDate(value) ? fallback : value;
}

function parseNumberParam(
  params: URLSearchParams,
  key: string,
  fallback: number | null
): number | null {
  const value = boundedParam(params, key, MAX_ARRAY_ITEM_LENGTH);
  if (value === null || value.trim() === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parsePositiveIntegerParam(
  params: URLSearchParams,
  key: string,
  fallback: number | null
): number | null {
  const parsed = parseNumberParam(params, key, fallback);
  return parsed !== null && Number.isSafeInteger(parsed) && parsed >= 1 ? parsed : fallback;
}

function parseJson(value: string | null): unknown {
  if (value === null || value.length > MAX_RAW_PARAM_LENGTH) return undefined;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return undefined;
  }
}

function parseEnumArray<const T extends readonly string[]>(
  params: URLSearchParams,
  key: string,
  allowed: T,
  fallback: readonly T[number][]
): T[number][] {
  const raw = boundedParam(params, key);
  if (raw === null) return [...fallback];
  const parsed = parseJson(raw);
  if (
    !Array.isArray(parsed) ||
    parsed.length > MAX_ARRAY_ITEMS ||
    parsed.some(
      (value) =>
        typeof value !== "string" ||
        value.length === 0 ||
        value.length > MAX_ARRAY_ITEM_LENGTH ||
        !allowed.includes(value)
    )
  ) {
    return [...fallback];
  }
  return uniqueSorted(parsed as T[number][]);
}

interface StringArrayOptions {
  preserveOrder?: boolean;
  uppercase?: boolean;
  knownColumns?: boolean;
}

function parseStringArray(
  params: URLSearchParams,
  key: string,
  fallback: readonly string[],
  options: StringArrayOptions = {}
): string[] {
  const raw = boundedParam(params, key);
  if (raw === null) return [...fallback];
  const parsed = parseJson(raw);
  if (
    !Array.isArray(parsed) ||
    parsed.length > MAX_ARRAY_ITEMS ||
    parsed.some(
      (value) =>
        typeof value !== "string" ||
        value.length === 0 ||
        value.length > MAX_ARRAY_ITEM_LENGTH ||
        UNSAFE_OBJECT_KEYS.has(value) ||
        (options.knownColumns && !SCREENER_COLUMN_ID_SET.has(value))
    )
  ) {
    return [...fallback];
  }
  const normalized = (parsed as string[]).map((value) =>
    options.uppercase ? value.trim().toUpperCase() : value
  );
  if (normalized.some((value) => value.length === 0)) return [...fallback];
  return options.preserveOrder ? uniqueInOrder(normalized) : uniqueSorted(normalized);
}

function parseSorts(params: URLSearchParams, fallback: readonly Readonly<ScreenerSort>[]): ScreenerSort[] {
  const raw = boundedParam(params, "sort");
  if (raw === null) return fallback.map((sort) => ({ ...sort }));
  const parsed = parseJson(raw);
  if (!Array.isArray(parsed) || parsed.length > MAX_SCREENER_SORTS) {
    return fallback.map((sort) => ({ ...sort }));
  }
  const sorts: ScreenerSort[] = [];
  for (const item of parsed) {
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
      return fallback.map((sort) => ({ ...sort }));
    }
    const candidate = item as Record<string, unknown>;
    if (
      typeof candidate.id !== "string" ||
      candidate.id.length === 0 ||
      candidate.id.length > MAX_ARRAY_ITEM_LENGTH ||
      !SORT_FIELDS.includes(candidate.id as ScreenerSortField) ||
      typeof candidate.desc !== "boolean"
    ) {
      return fallback.map((sort) => ({ ...sort }));
    }
    sorts.push({ id: candidate.id as ScreenerSortField, desc: candidate.desc });
  }
  return normalizeSorts(sorts) ?? fallback.map((sort) => ({ ...sort }));
}

function parseVisibility(
  params: URLSearchParams,
  fallback: Readonly<Record<string, boolean>>
): Record<string, boolean> {
  const raw = boundedParam(params, "columns");
  if (raw === null) return { ...fallback };
  const parsed = parseJson(raw);
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return { ...fallback };
  const entries = Object.entries(parsed);
  if (
    entries.length > MAX_ARRAY_ITEMS ||
    entries.some(
      ([key, value]) =>
        key === "" ||
        key.length > MAX_ARRAY_ITEM_LENGTH ||
        UNSAFE_OBJECT_KEYS.has(key) ||
        !SCREENER_COLUMN_ID_SET.has(key) ||
        typeof value !== "boolean"
    )
  ) {
    return { ...fallback };
  }
  const visibility: Record<string, boolean> = {};
  for (const [key, value] of entries.sort(([left], [right]) => left.localeCompare(right))) {
    visibility[key] = value as boolean;
  }
  return visibility;
}

function parseView(params: URLSearchParams): BuiltInViewId {
  const value = boundedParam(params, "view", MAX_ARRAY_ITEM_LENGTH);
  return value !== null && BUILT_IN_VIEW_IDS.includes(value as BuiltInViewId)
    ? (value as BuiltInViewId)
    : DEFAULT_SCREENER_STATE.builtInViewId;
}

function parseNullableId(params: URLSearchParams, key: string, fallback: string | null): string | null {
  const value = boundedParam(params, key, MAX_ID_LENGTH);
  return value === null || value.trim() === "" ? fallback : value;
}

export function parseScreenerState(search: string): ScreenerState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  if (boundedParam(params, "v", 16) !== "1") return createDefaultScreenerState();

  const defaults = createDefaultScreenerState();
  const builtInViewId = parseView(params);
  const defaultSort = BUILT_IN_VIEWS[builtInViewId].defaultSort;
  const pageValue = boundedParam(params, "page", MAX_ARRAY_ITEM_LENGTH);
  const parsedPage = pageValue === null || pageValue.trim() === "" ? Number.NaN : Number(pageValue);
  const page =
    Number.isSafeInteger(parsedPage) && parsedPage >= 0 && parsedPage <= MAX_PAGE
      ? parsedPage
      : defaults.page;
  const pageSizeValue = Number(boundedParam(params, "page_size", MAX_ARRAY_ITEM_LENGTH));
  const pageSize = PAGE_SIZES.includes(pageSizeValue as ScreenerPageSize)
    ? (pageSizeValue as ScreenerPageSize)
    : defaults.pageSize;
  const densityValue = boundedParam(params, "density", MAX_ARRAY_ITEM_LENGTH);
  const density = DENSITIES.includes(densityValue as ScreenerDensity)
    ? (densityValue as ScreenerDensity)
    : defaults.density;
  const favoritesValue = boundedParam(params, "favorites", 16);
  const favoritesOnly =
    favoritesValue === null
      ? defaults.filters.favoritesOnly
      : favoritesValue === "1"
        ? true
        : favoritesValue === "0"
          ? false
          : defaults.filters.favoritesOnly;

  return {
    filters: {
      query: parseBoundedText(params, "q", defaults.filters.query, MAX_TEXT_LENGTH),
      filingDateFrom: parseDateParam(params, "filing_from", defaults.filters.filingDateFrom),
      filingDateTo: parseDateParam(params, "filing_to", defaults.filters.filingDateTo),
      tradeDateFrom: parseDateParam(params, "trade_from", defaults.filters.tradeDateFrom),
      tradeDateTo: parseDateParam(params, "trade_to", defaults.filters.tradeDateTo),
      signalBuckets: parseEnumArray(
        params,
        "signals",
        SIGNAL_BUCKETS,
        defaults.filters.signalBuckets
      ),
      relationshipFlags: parseEnumArray(
        params,
        "relationships",
        RELATIONSHIP_FLAGS,
        defaults.filters.relationshipFlags
      ),
      minTransactionValue: parseNumberParam(
        params,
        "value_min",
        // Noise-control defaults apply only to the product default view.
        builtInViewId === DEFAULT_SCREENER_STATE.builtInViewId
          ? defaults.filters.minTransactionValue
          : null
      ),
      maxTransactionValue: parseNumberParam(
        params,
        "value_max",
        defaults.filters.maxTransactionValue
      ),
      planStatuses: parseEnumArray(params, "plans", PLAN_STATUSES, defaults.filters.planStatuses),
      minDistinctInsiders: parsePositiveIntegerParam(
        params,
        "insiders_min",
        defaults.filters.minDistinctInsiders
      ),
      exchanges: parseStringArray(params, "exchanges", defaults.filters.exchanges, {
        uppercase: true
      }),
      transactionActivities: parseEnumArray(
        params,
        "activities",
        ACTIVITIES,
        defaults.filters.transactionActivities
      ),
      transactionCodes: parseStringArray(
        params,
        "codes",
        defaults.filters.transactionCodes,
        { uppercase: true }
      ),
      securityStatuses: parseEnumArray(
        params,
        "securities",
        SECURITY_STATUSES,
        builtInViewId === DEFAULT_SCREENER_STATE.builtInViewId
          ? defaults.filters.securityStatuses
          : []
      ),
      minShares: parseNumberParam(params, "shares_min", defaults.filters.minShares),
      maxShares: parseNumberParam(params, "shares_max", defaults.filters.maxShares),
      minPrice: parseNumberParam(params, "price_min", defaults.filters.minPrice),
      maxPrice: parseNumberParam(params, "price_max", defaults.filters.maxPrice),
      minHoldingsChange: parseNumberParam(
        params,
        "holdings_min",
        defaults.filters.minHoldingsChange
      ),
      maxHoldingsChange: parseNumberParam(
        params,
        "holdings_max",
        defaults.filters.maxHoldingsChange
      ),
      confidences: parseEnumArray(
        params,
        "confidences",
        CONFIDENCES,
        defaults.filters.confidences
      ),
      amendmentStates: parseEnumArray(
        params,
        "amendments",
        AMENDMENT_STATES,
        defaults.filters.amendmentStates
      ),
      valueQualities: parseEnumArray(
        params,
        "qualities",
        VALUE_QUALITIES,
        defaults.filters.valueQualities
      ),
      missingFieldFlags: parseEnumArray(
        params,
        "missing",
        MISSING_FIELDS,
        defaults.filters.missingFieldFlags
      ),
      industryDescription: parseBoundedText(
        params,
        "industry",
        defaults.filters.industryDescription,
        MAX_TEXT_LENGTH
      ),
      minFilingLagDays: parseNumberParam(
        params,
        "lag_min",
        defaults.filters.minFilingLagDays
      ),
      maxFilingLagDays: parseNumberParam(
        params,
        "lag_max",
        defaults.filters.maxFilingLagDays
      ),
      accessionNumber: parseBoundedText(
        params,
        "accession",
        defaults.filters.accessionNumber,
        MAX_ID_LENGTH
      ),
      favoritesOnly,
      marketCapBuckets: parseEnumArray(
        params,
        "mcap",
        ["micro", "small", "mid", "large", "mega", "unknown"] as const,
        defaults.filters.marketCapBuckets
      ),
      minValueToMarketCapBps: parseNumberParam(
        params,
        "mcap_bps_min",
        defaults.filters.minValueToMarketCapBps
      )
    },
    sorting: parseSorts(params, defaultSort),
    page,
    pageSize,
    density,
    columnVisibility: parseVisibility(params, defaults.columnVisibility),
    columnOrder:
      normalizeColumnOrder(
        parseStringArray(params, "column_order", defaults.columnOrder, {
          preserveOrder: true,
          knownColumns: true
        })
      ) ?? [...defaults.columnOrder],
    columnPinning: {
      left: parseStringArray(params, "pin_left", defaults.columnPinning.left, {
        preserveOrder: true,
        knownColumns: true
      }),
      right: parseStringArray(params, "pin_right", defaults.columnPinning.right, {
        preserveOrder: true,
        knownColumns: true
      })
    },
    builtInViewId,
    savedViewId: parseNullableId(params, "saved", defaults.savedViewId),
    selectedTransactionId: parseNullableId(params, "tx", defaults.selectedTransactionId)
  };
}
